import { useEffect, useState } from 'react';
import { getTechnicianPrivilegesApiService } from '../../api/getTechnicianPrivilegesApiService';
import type { TechnicianPrivileges } from '../../types/addTechnicianType';
import { TECHNICIAN_LABELS } from '../../constants';

interface ModuleItem {
  key: keyof TechnicianPrivileges | null;
  label: string;
  alwaysChecked?: true;
}

interface ModuleConfig {
  title: string;
  items: ModuleItem[];
}

const MODULES: ModuleConfig[] = [
  {
    title: TECHNICIAN_LABELS.MODULE_MANAGE_TASKS,
    items: [
      {
        key: null,
        label: TECHNICIAN_LABELS.PRIV_TASK_LIST,
        alwaysChecked: true,
      },
      { key: 'taskView', label: TECHNICIAN_LABELS.PRIV_TASK_VIEW },
      {
        key: 'taskUpdateStatus',
        label: TECHNICIAN_LABELS.PRIV_TASK_UPDATE_STATUS,
      },
      { key: 'taskFillForm', label: TECHNICIAN_LABELS.PRIV_TASK_FILL_FORM },
      {
        key: 'taskAcceptReject',
        label: TECHNICIAN_LABELS.PRIV_TASK_ACCEPT_REJECT,
      },
    ],
  },
  {
    title: TECHNICIAN_LABELS.MODULE_CERTIFICATES,
    items: [
      { key: 'certificateList', label: TECHNICIAN_LABELS.PRIV_CERT_LIST },
      { key: 'certificateView', label: TECHNICIAN_LABELS.PRIV_CERT_VIEW },
      {
        key: 'certificateDownload',
        label: TECHNICIAN_LABELS.PRIV_CERT_DOWNLOAD,
      },
    ],
  },
  {
    title: TECHNICIAN_LABELS.MODULE_INVOICES,
    items: [
      { key: 'invoiceList', label: TECHNICIAN_LABELS.PRIV_INVOICE_LIST },
      { key: 'invoiceView', label: TECHNICIAN_LABELS.PRIV_INVOICE_VIEW },
      {
        key: 'invoiceDownload',
        label: TECHNICIAN_LABELS.PRIV_INVOICE_DOWNLOAD,
      },
    ],
  },
];

function getEnabledItems(
  privileges: TechnicianPrivileges,
  module: ModuleConfig
): ModuleItem[] {
  return module.items.filter(
    (item) => item.alwaysChecked || (item.key !== null && privileges[item.key])
  );
}

interface ViewTechnicianPermissionsProps {
  technicianId: string;
}

const ViewTechnicianPermissions = ({
  technicianId,
}: ViewTechnicianPermissionsProps) => {
  const [privileges, setPrivileges] = useState<TechnicianPrivileges | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    getTechnicianPrivilegesApiService(technicianId)
      .then((result) => {
        if (result) {
          setPrivileges(result);
        } else {
          setError('Nie udało się załadować uprawnień.');
        }
      })
      .catch(() => setError('Nie udało się załadować uprawnień.'))
      .finally(() => setIsLoading(false));
  }, [technicianId]);

  const modulesWithEnabled = privileges
    ? MODULES.map((module) => ({
        title: module.title,
        enabled: getEnabledItems(privileges, module),
      })).filter((module) => module.enabled.length > 0)
    : [];

  return (
    <div className="flex flex-col gap-6 m-[6px]">
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          aria-label="Uprawnienia"
          data-testid="view-technician-permissions-title"
        >
          Uprawnienia
        </p>

        {isLoading ? (
          <div
            className="flex flex-col gap-6"
            data-testid="view-technician-permissions-loading"
          >
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 4 - (i % 2) }).map((_, j) => (
                    <div
                      key={j}
                      className="h-8 bg-gray-200 rounded-[8px] animate-pulse"
                      style={{ width: `${72 + ((j * 16) % 48)}px` }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p
            className="text-bodyMedium text-on-surface-variant"
            data-testid="view-technician-permissions-error"
          >
            {error}
          </p>
        ) : modulesWithEnabled.length === 0 ? (
          <p
            className="text-bodyMedium text-on-surface-variant"
            data-testid="view-technician-permissions-empty"
          >
            Brak danych do wyświetlenia.
          </p>
        ) : (
          <div
            className="flex flex-col gap-8"
            data-testid="view-technician-permissions-content"
          >
            {modulesWithEnabled.map((module) => (
              <div key={module.title} className="flex flex-col gap-3">
                <p className="text-bodySmall font-normal text-on-surface-variant">
                  {module.title}
                </p>
                <div className="flex flex-wrap gap-2">
                  {module.enabled.map((item) => (
                    <span
                      key={item.key ?? 'task-list-fixed'}
                      className="inline-flex items-center justify-center h-8 px-3 rounded-[8px] bg-information-90 text-information-30 text-labelLarge"
                      data-testid={`view-technician-permission-${item.key ?? 'taskList'}`}
                    >
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewTechnicianPermissions;
